import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PermissionService } from '../services/permission/permission.service';

/**
 * Route guard that checks if the user has permission to access a route.
 *
 * Usage in routes:
 * {
 *   path: 'products',
 *   canActivate: [MsalGuard, permissionGuard],
 *   loadChildren: () => import('./inventory/products/products.module').then(m => m.ProductsModule)
 * }
 *
 * The guard extracts the route path and checks if it exists in the user's
 * permitted menu items. If not, redirects to /unauthorized.
 */
export const permissionGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): Observable<boolean | UrlTree> => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  // Build the full path from the route
  let routePath = getRoutePath(route);

  // Map routes from breadcrumb ignoreList to their actual permission paths
  routePath = mapRouteToPermissionPath(routePath);

  // Special routes that should always be accessible (after MSAL auth)
  const alwaysAllowedRoutes = [
    'profile',
    'unauthorized'
  ];

  if (alwaysAllowedRoutes.includes(routePath)) {
    return permissionService.loadMenuItems().pipe(map(() => true));
  }

  return permissionService.hasAccess(routePath).pipe(
    map(hasAccess => {
      if (hasAccess) {
        return true;
      }
      // Redirect to unauthorized page
      return router.createUrlTree(['/unauthorized']);
    })
  );
};

/**
 * Extracts the top-level route path from the ActivatedRouteSnapshot.
 * Returns the immediate route segment for permission checking.
 */
function getRoutePath(route: ActivatedRouteSnapshot): string {
  // Get the path from the route config
  const path = route.routeConfig?.path || '';

  // If no path, try to get from URL segments
  if (!path && route.url.length > 0) {
    return route.url.map(segment => segment.path).join('/');
  }

  return path;
}

/**
 * Maps route paths to their actual permission paths based on breadcrumb ignoreList.
 * This ensures the permission guard checks the correct route for aliased paths.
 * 
 * @param routePath The route path to map
 * @returns The mapped permission path
 */
function mapRouteToPermissionPath(routePath: string): string {
  // Route mappings from breadcrumb ignoreList
  // These routes are aliases that should check permissions for different paths
  const routeMappings: { [key: string]: string } = {
    'issue-payments': 'payments',
    'issue-receipts': 'receipts',
    'payment-methods': 'shops'
  };

  // Check if the route path needs mapping
  if (routeMappings[routePath]) {
    return routeMappings[routePath];
  }

  // Check if route path starts with any mapped route (for nested routes)
  for (const [alias, actualPath] of Object.entries(routeMappings)) {
    if (routePath.startsWith(alias + '/')) {
      return routePath.replace(alias, actualPath);
    }
  }

  return routePath;
}
